import emba from 'images/select-program/emba.png';
import mba from 'images/select-program/mba.png';
import msba from 'images/select-program/msba.png';
import msaie from 'images/select-program/msaie.png';
import msse from 'images/select-program/msse.png';
import aiTechLeaders from 'images/select-program/ai-tech-leaders.png';
import aiBusiness from 'images/select-program/ai-business.png';
import busAnalyticsLeaders from 'images/select-program/bus-analytics-leaders.png';
import cmoProgram from 'images/select-program/cmo-program.png';
import ctoProgram from 'images/select-program/cto-program.png';
import dataScienceFoundations from 'images/select-program/data-sci-foundations.png';
import executiveProgram from 'images/select-program/executive-program.png';
import financeAccountingManagers from 'images/select-program/finance-accounting-managers.png';
import foundersProgram from 'images/select-program/founders-program.png';
import prototypeGpt from 'images/select-program/prototype-gpt.png';
import tybAi from 'images/select-program/tyb-ai.png';
import tybBlockchain from 'images/select-program/tyb-blockchain.png';

import { InstitutionId } from 'Institutions';
import { Brand, formattedBrandName } from 'AppBranding';
import { ProgramPreferencesStateKey } from 'Careers';
import { SelectProgramSection } from 'Settings/angularModule/scripts/select_program/selectProgram.types';
import { ExecEdCertCategory } from 'ExecEdEligibility';
import { ADDITIONAL_FINANCING_OPTIONS } from 'CohortSectionOffer/constants';
import { QUANTIC_DOMAIN, QUANTIC_STUDENT_EMAIL_DOMAIN, SMARTLY_DOMAIN, VALAR_DOMAIN } from 'PedagoDomainConstants';
import { ApplicationFormConfigsByProgramFamily, ApplicationFormConfigsByProgramType } from 'FormConfigs';
import { PHYSICAL_DIPLOMA, DELIVERED_DIGITAL_CERTIFICATE, DYNAMIC_DOWNLOADABLE_CERTIFICATE } from './constants';
import buildConfig from './buildConfig';
import { type CohortContext, type ProgramTypeConfig, ProgramFamily, ProgramType, ProgramField } from './Program.types';
import {
    isPreMsbaAppliedAiRename,
    MSBA_PRE_RENAME_FULL_TITLE,
    MSBA_POST_RENAME_FULL_TITLE,
    MSBA_PRE_RENAME_LONG_TITLE,
    MSBA_POST_RENAME_LONG_TITLE,
    MSBA_PRE_RENAME_BUSINESS_ANALYTICS_TAIL,
    MSBA_POST_RENAME_BUSINESS_ANALYTICS_TAIL,
    MSBA_PRE_RENAME_ACHIEVEMENT_GRAPHIC_LINES,
    MSBA_POST_RENAME_ACHIEVEMENT_GRAPHIC_LINES,
} from './getCohortAwareMsbaTitles';
import { welcomePackageConfigs } from './welcomePackageConfigs';
import {
    mbaAndEmbaStudentSpotlightCarouselProps,
    valarMbaAndEmbaStudentSpotlightCarouselProps,
} from './studentSpotlightCarouselProps';

/*

    How to move a property from schedulable.js to here:

    1. Determine the current value in schedulable.js for each program type. This can be done either by reading the code in schedulable.js
        or by using the `audit` tool defined in schedulable.js (search there for instructions)
    2. Add the property to the ProgramTypeConfig type
    3. Determine which level(s) the value should be set at. If this is something specific for each program, like `label`, then
        it would be set in the configuration for the specific program type. If, for example, all of the degree programs share
        the same value, then it could be set in DefaultDegreeConfig

    4. Set the property in the appropriate places in this file
        * dynamic properties must be created by assigning a function as the value. The argument to the function will
            be the full config object, making it possible to build certain values by combining other values on the object.
            For an example, see `networkProgramTitle`.
        * NOTE: If you don't make dynamic properties a function, TS will error saying the property is incompatible because it's readonly.
    5. Remove all the places in schedulable.js where the property is set. Do not remove calls to delegateToProgramType
        or references in schedulable.js that use the property to determine other things.
    5. Check that you have not accidentally changed any values:
        * In `main` and in your branch, run `console.log(JSON.stringify($('[ng-controller]').injector().get('Cohort').audit(keys)))`
        * Compare the results. One way to do this is to copy the results to files in vscode, format as json, and use the "Compare Selected" tool

*/

const DefaultConfig = {
    isMBA: false,
    isEMBA: false,
    isMSBA: false,
    isMSSE: false,
    isMSAIE: false,
    isExecEd: false,
    numMonths: null,
    isDegreeProgram: false,
    supportsCohortPreApproval: false,
    // Only Quantic degree programs issue student email addresses (overridden in DefaultDegreeConfig).
    studentEmailDomain: null,
    // The profile-photo upload section shows on Settings > Account for all programs except where overridden.
    supportsAvatarUploadInAccountPage: true,
    supportsSchedule: false,
    supportsScheduleSidebarBox: () => false,
    supportsScheduleStatusSidebarBox: () => false,
    supportsEnrollmentSidebar: () => false,
    supportsNetworkAccess: false,
    networkProgramTitle: null,
    requiresTILADisclosure: false,
    supportsBotPage: false,
    supportsSlackRooms: false,
    completionDocumentType: null,
    requiresProctoring: false,
    showApplicationScholarshipEligibilityNotice: false,
    showFundingEmail: false,
    supportsBuyNowPayLater: false,
    supportsStudentDashboardConfig: true,
    supportsTranscriptDownload: false,
    supportsProgramApplications: false,
    supportsManuallyApplyingToProgram: false,
    usersCanInitiateProgramApplications: false,
    applicationFormConfigs: [],
    defaultAutoDeclineAdmissionOffersDaysOffset: null,
    supportsActivateExecEdDates: false,
    supportsCourseSummary: false,
    supportsEnrollmentVerificationLetter: false,
    standaloneShortProgramTitle: ({ shortProgramTitle }: ProgramTypeConfig) => shortProgramTitle,
    applicationProgramTypeHeader: null,

    // Because of how we're using these `programPreferenceStateKeys` in `determineTargetProgramType`,
    // there can't be any overlap with the `programPreferenceStateKeys` in other program type configs
    // belonging to the same institution.
    programPreferenceStateKeys: () => [],
    supportsSharedExternalScheduleUrl: false,
    welcomePackageConfiguration: null,
    studentSpotlightCarouselProps: { streamIds: [] } as ProgramTypeConfig['studentSpotlightCarouselProps'],
    selectProgramListItem: null,
    supportsAdditionalFinancingOptions: false,
    additionalFinancingOptions: [] as ProgramTypeConfig['additionalFinancingOptions'],
    supportEmailDomain: null,
    supportEmailUsername: null,
    enrollmentFaqLink: null,
    supportsWelcomeBox: false,
    supportsGradebook: false,
    supportsDocumentUpload: false,
    supportsEditingCareerProfile: false,
    supportsResourcesTab: false,
    supportsAdmissionRounds: false,
    postApplyInterviewModalLocaleKeys: {
        instant: {
            nextStepKey: 'next_step_speak_to_admissions',
            selectivityMessageKey: 'selectivity_message_require_conversation',
        },
        optional: {
            nextStepKey: 'next_step_speak_to_admissions',
            selectivityMessageKey: 'selectivity_message_strengthen_application',
        },
    },
} as const;

const DefaultDegreeConfig = {
    ...DefaultConfig,
    isDegreeProgram: true,
    studentEmailDomain: QUANTIC_STUDENT_EMAIL_DOMAIN,
    learnMoreUrlBrandConfigProperty: 'learnMoreAboutProgramsUrl',
    supportsNetworkAccess: true,
    supportsSchedule: true,
    supportsScheduleSidebarBox: () => true,
    supportsScheduleStatusSidebarBox: () => true,
    supportsEnrollmentSidebar: () => true,
    supportsBotPage: true,
    requiresTILADisclosure: true,
    transcriptProgramTitle: () => null,
    networkProgramTitle: ({ branding, shortProgramTitle }: ProgramTypeConfig) =>
        `${formattedBrandName('short', branding)} ${shortProgramTitle}`,
    showFundingEmail: true,
    supportsSlackRooms: true,
    completionDocumentType: PHYSICAL_DIPLOMA,
    requiresProctoring: true,
    supportsProgramApplications: true,
    supportsManuallyApplyingToProgram: true,
    usersCanInitiateProgramApplications: true,
    supportsGradebook: true,
    supportsDocumentUpload: true,
    supportsEditingCareerProfile: true,
    supportsResourcesTab: true,
    supportsAdmissionRounds: true,
    supportsActivateExecEdDates: true,
    supportsEnrollmentVerificationLetter: true,
    defaultAutoDeclineAdmissionOffersDaysOffset: -3, // The Friday before the start of the cohort
    supportsCourseSummary: true,
    supportsTranscriptDownload: true,
    supportsAdditionalFinancingOptions: true,
    enrollmentFaqLink: `/help/enrollment-verification-and-documentation`,
} as const;

const DefaultQuanticConfig = {
    institutionID: InstitutionId.quantic,
    branding: Brand.quantic,
    supportEmailDomain: QUANTIC_DOMAIN,
    supportEmailUsername: 'support',
    projectEmail: 'projects@quantic.edu',
} as const;

const DefaultValarConfig = {
    institutionID: InstitutionId.valar,
    branding: Brand.valar,
    supportEmailDomain: VALAR_DOMAIN,
    supportEmailUsername: 'support',
    projectEmail: 'projects@valar.quantic.edu',
} as const;

const DefaultSmartlyConfig = {
    institutionID: InstitutionId.smartly,
    branding: Brand.smartly,
    supportEmailDomain: SMARTLY_DOMAIN,
    supportEmailUsername: 'support',
} as const;

// I'm assuming we'll need this. If this is still empty once everything is
// copied over, then remove it
const DefaultCertificateConfig = {
    ...DefaultConfig,
    supportEmailDomain: QUANTIC_DOMAIN,
    supportEmailUsername: 'support',
    enrollmentFaqLink: `/help/enrollment-verification-and-documentation`,
    // Certificate programs (including ExecEd certs and TheBusinessCertificate) transform the cohort's
    // title (e.g. `"CTO COHORT 5"`) for the accepted-screen and dashboard-box HTML.
    acceptedScreenTitleHtml(cohort: CohortContext | null) {
        if (!cohort) return null;
        const upcased = cohort.title.toUpperCase();
        if (upcased.endsWith('CERTIFICATE')) {
            return upcased.replace('CERTIFICATE', '<br class="max-sm:hidden">CERTIFICATE');
        }
        return upcased;
    },
    studentDashboardProgramBoxTitleHtml(cohort: CohortContext | null) {
        return cohort?.title ?? null;
    },
} as const;

const DefaultExecEdConfig = {
    ...DefaultCertificateConfig,
    ...DefaultQuanticConfig,
    programFamily: ProgramFamily.exec_ed_certificate,
    learnMoreUrlBrandConfigProperty: 'learnMoreAboutProgramsUrl',
    supportsSchedule: true,
    supportsScheduleSidebarBox: () => true,
    supportsScheduleStatusSidebarBox: () => true,
    supportsEnrollmentSidebar: () => true,
    isExecEd: true,
    supportsCohortPreApproval: true,
    supportsNetworkAccess: true,
    supportsBotPage: true,
    networkProgramTitle: (config: ProgramTypeConfig) => `Quantic Executive Education: ${config.fullTitle(null)}`,
    // ExecEd certs reuse `fullTitle` as their program box title — `"The Quantic CTO Program"` would be
    // too long for the dashboard box at mobile widths, so the standalone short form is what we want.
    programBoxTitle(this: ProgramTypeConfig, cohort: CohortContext | null) {
        return this.fullTitle(cohort);
    },
    supportsSlackRooms: false, // We may want to add slack to some exec ed programs in the future, but for now it's not supported
    completionDocumentType: DELIVERED_DIGITAL_CERTIFICATE,
    showFundingEmail: false,
    supportsBuyNowPayLater: true,
    supportsProgramApplications: true,
    usersCanInitiateProgramApplications: true,
    defaultAutoDeclineAdmissionOffersDaysOffset: -5, // Cohort.DEFAULT_REGISTRATION_DEADLINE_DAYS_OFFSET + 2 (see cohort.js)
    supportsSharedExternalScheduleUrl: true,
    supportsEnrollmentVerificationLetter: true,
    welcomePackageConfiguration: welcomePackageConfigs.execEd,
    libraryDatabases: [] as ProgramTypeConfig['libraryDatabases'],
    studentSpotlightCarouselProps: mbaAndEmbaStudentSpotlightCarouselProps,
    supportEmailDomain: QUANTIC_DOMAIN,
    supportEmailUsername: 'support',
    supportsGradebook: true,
    supportsEditingCareerProfile: true,
    supportsResourcesTab: true,
    supportsAdmissionRounds: true,
} as const;

// Two-month ExecEd certs starting cycle 77+ use a lighter dashboard: the
// period-schedule, schedule-status, and enrollment sidebar boxes are hidden.
// Pre-cycle-77 cohorts still show them. See https://trello.com/c/syVk1gUw
const isPreCycle77TwoMonthCohort = (cohort: CohortContext | null): boolean => {
    if (!cohort) return true;
    const startDate = cohort.startDate instanceof Date ? cohort.startDate : new Date(1000 * cohort.startDate);
    // `Cohort['C-TBIZAI-77'].start_date` is 2026-06-08T08:00:00Z. Compare against the
    // exact unix timestamp rather than `new Date('2026/06/08')`, which would parse
    // in the runtime's local timezone and could misclassify cohorts straddling the
    // boundary.
    return startDate < new Date(1780905600000);
};

const DefaultTwoMonthExecEdConfig = {
    ...DefaultExecEdConfig,
    numMonths: 2,
    supportsScheduleSidebarBox: isPreCycle77TwoMonthCohort,
    supportsScheduleStatusSidebarBox: isPreCycle77TwoMonthCohort,
    supportsEnrollmentSidebar: isPreCycle77TwoMonthCohort,
} as const;

const DefaultVLAProgramTypeConfig = {
    ...DefaultConfig,
    programField: ProgramField.valar_leadership_accelerator,
    programFamily: ProgramFamily.valar_leadership_accelerator,
    institutionID: InstitutionId.valar,
    supportsSchedule: true,
    supportsScheduleSidebarBox: () => true,
    supportsScheduleStatusSidebarBox: () => true,
    branding: Brand.valar,
    shortProgramTitle: 'VLA',
    supportsStudentDashboardConfig: true,

    // See Program/constants.ts for details on the VLA certificate
    completionDocumentType: DELIVERED_DIGITAL_CERTIFICATE,
    libraryDatabases: [] as ProgramTypeConfig['libraryDatabases'],
    supportEmailDomain: VALAR_DOMAIN,
    supportEmailUsername: 'support',
} as const;

const isEMBA = {
    programField: ProgramField.master_of_business_administration,
    programFamily: ProgramFamily.master_of_business_administration,
    applicationFormConfigs: ApplicationFormConfigsByProgramFamily[ProgramFamily.master_of_business_administration],
    isEMBA: true,
    learnMoreUrlBrandConfigProperty: 'learnMoreAboutEmbaUrl',
    shortProgramTitle: 'EMBA',
    showApplicationScholarshipEligibilityNotice: true,
    fullTitle: () => 'Executive MBA',
    libraryDatabases: ['statista', 'one_business'] as ProgramTypeConfig['libraryDatabases'],
    openCoursesSidebarBoxContentKey: 'content_open_courses_business_admin',
    applicationReceivedModalContinueCoursesKey: 'continue_courses_business_admin',
    programBoxNotAppliedMobileHtmlKey: 'mba_programs',
    applicationReceivedBoxPlaylistTitleKey: 'businessAdmin',

    // See comment for `programPreferenceStateKeys` in `DefaultConfig`.
    programPreferenceStateKeys: () => [
        ProgramPreferencesStateKey.business_admin_middle_experience,
        ProgramPreferencesStateKey.business_admin_younger_with_high_experience,
        ProgramPreferencesStateKey.business_admin_older_with_high_experience,
    ],
} as const;

const isMBA = {
    programField: ProgramField.master_of_business_administration,
    programFamily: ProgramFamily.master_of_business_administration,
    applicationFormConfigs: ApplicationFormConfigsByProgramFamily[ProgramFamily.master_of_business_administration],
    isMBA: true,
    // This is used in the admissions process modals before submitting application,
    // and we use the EMBA site even when a signup's default program type is MBA
    learnMoreUrlBrandConfigProperty: 'learnMoreAboutEmbaUrl',
    shortProgramTitle: 'MBA',
    showApplicationScholarshipEligibilityNotice: true,
    fullTitle: () => 'MBA',
    libraryDatabases: ['statista', 'one_business'] as ProgramTypeConfig['libraryDatabases'],
    openCoursesSidebarBoxContentKey: 'content_open_courses_business_admin',
    applicationReceivedModalContinueCoursesKey: 'continue_courses_business_admin',
    programBoxNotAppliedMobileHtmlKey: 'mba_programs',
    applicationReceivedBoxPlaylistTitleKey: 'businessAdmin',

    // See comment for `programPreferenceStateKeys` in `DefaultConfig`.
    programPreferenceStateKeys: () => [
        ProgramPreferencesStateKey.business_admin_minimum_experience,
        ProgramPreferencesStateKey.business_admin_younger_with_low_experience,
        ProgramPreferencesStateKey.business_admin_older_with_low_experience,
    ],
} as const;

export const ProgramTypeConfigs: Record<ProgramType, ProgramTypeConfig> = {
    /* Quantic Degree Programs */
    [ProgramType.emba]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultQuanticConfig,
        ...isEMBA,
        key: ProgramType.emba,
        label: 'EMBA', // internal
        supportEmailUsername: 'emba',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_mba_emba',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1650 * 15,
            numMonthlyIntervals: 15,
        },
        welcomePackageConfiguration: welcomePackageConfigs.emba,
        transcriptProgramTitle: () => 'Executive Master of Business Administration',
        studentSpotlightCarouselProps: mbaAndEmbaStudentSpotlightCarouselProps,
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: emba,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_emba' },
            description: { key: 'settings.select_program.program_emba_description' },
            learnMoreUrl: 'https://quantic.edu/executive-mba/',
            programType: ProgramType.emba,
            isVisible: (institutionID: InstitutionId) => institutionID === InstitutionId.quantic,
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Quantic / 30 months / 14% APR', 'Quantic / 60 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: () => 'The Quantic EMBA',
        programSwitcherMenuTitle: () => 'Executive Master of Business Administration',
        programAchievementGraphicProgramTitleLines: () => ['Executive MBA'],
        acceptedScreenTitleHtml: () => 'Executive MBA',
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic <br class="max-md:hidden"> EMBA',
    }),
    [ProgramType.mba]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultQuanticConfig,
        ...isMBA,
        key: ProgramType.mba,
        label: 'MBA', // internal
        supportEmailUsername: 'mba',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_mba_emba',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1650 * 15,
            numMonthlyIntervals: 15,
        },
        welcomePackageConfiguration: welcomePackageConfigs.mba,
        transcriptProgramTitle: () => 'Master of Business Administration',
        studentSpotlightCarouselProps: mbaAndEmbaStudentSpotlightCarouselProps,
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: mba,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_mba' },
            description: { key: 'settings.select_program.program_mba_description' },
            learnMoreUrl: 'https://quantic.edu/mba/',
            programType: ProgramType.mba,
            isVisible: (institutionID: InstitutionId) => institutionID === InstitutionId.quantic,
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Quantic / 30 months / 14% APR', 'Quantic / 60 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: () => 'The Quantic MBA',
        programSwitcherMenuTitle: () => 'Master of Business Administration',
        programAchievementGraphicProgramTitleLines: () => ['MBA'],
        acceptedScreenTitleHtml: () => 'MBA',
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic <br class="max-md:hidden"> MBA',
    }),
    [ProgramType.msba]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultQuanticConfig,
        programFamily: ProgramFamily.master_of_science_in_business_analytics,
        applicationFormConfigs:
            ApplicationFormConfigsByProgramFamily[ProgramFamily.master_of_science_in_business_analytics],
        key: ProgramType.msba,
        programField: ProgramField.master_of_science_in_business_analytics,
        isMSBA: true,
        learnMoreUrlBrandConfigProperty: 'learnMoreAboutMsbaUrl',
        label: 'MSBA', // internal
        supportEmailUsername: 'msba',
        shortProgramTitle: 'MSBA',
        fullTitle: (cohort: CohortContext | null) =>
            cohort && isPreMsbaAppliedAiRename(cohort) ? MSBA_PRE_RENAME_FULL_TITLE : MSBA_POST_RENAME_FULL_TITLE,
        openCoursesSidebarBoxContentKey: 'content_open_courses_business_analytics',
        applicationReceivedModalContinueCoursesKey: 'continue_courses_business_analytics',
        programBoxNotAppliedMobileHtmlKey: 'msba_program',
        applicationReceivedBoxPlaylistTitleKey: 'businessAnalytics',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_msba',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1650 * 13,
            numMonthlyIntervals: 13,
        },
        welcomePackageConfiguration: welcomePackageConfigs.msba,
        transcriptProgramTitle: (cohort: CohortContext | null) =>
            cohort && isPreMsbaAppliedAiRename(cohort) ? MSBA_PRE_RENAME_LONG_TITLE : MSBA_POST_RENAME_LONG_TITLE,
        programSwitcherMenuTitle: (cohort: CohortContext | null) =>
            cohort && isPreMsbaAppliedAiRename(cohort) ? MSBA_PRE_RENAME_LONG_TITLE : MSBA_POST_RENAME_LONG_TITLE,
        programAchievementGraphicProgramTitleLines: (cohort: CohortContext | null) =>
            cohort && isPreMsbaAppliedAiRename(cohort)
                ? [...MSBA_PRE_RENAME_ACHIEVEMENT_GRAPHIC_LINES]
                : [...MSBA_POST_RENAME_ACHIEVEMENT_GRAPHIC_LINES],
        acceptedScreenTitleHtml: (cohort: CohortContext | null) =>
            cohort && isPreMsbaAppliedAiRename(cohort) ? MSBA_PRE_RENAME_FULL_TITLE : MSBA_POST_RENAME_FULL_TITLE,
        studentDashboardProgramBoxTitleHtml: (cohort: CohortContext | null) =>
            `The Quantic MS in <br class="max-md:hidden"> ${
                cohort && isPreMsbaAppliedAiRename(cohort)
                    ? MSBA_PRE_RENAME_BUSINESS_ANALYTICS_TAIL
                    : MSBA_POST_RENAME_BUSINESS_ANALYTICS_TAIL
            }`,
        libraryDatabases: ['statista', 'one_business'],
        studentSpotlightCarouselProps: {
            streamIds: [
                '8889a693db6348253a1c6b997299506c',
                '8d26a4bb2df39b1a4ef4b5b6cb662448',
                '50fb20d27a45adffd063700b7f5b3327',
                '15dec0e73019fc35d840529e7a3e987a',
                '020574561588721776f193ad4a2205a7',
            ],
        },
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: msba,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_msba' },
            description: { key: 'settings.select_program.program_msba_description' },
            preapprovedDescription: { key: 'settings.select_program.program_msba_preapproved_description' },
            learnMoreUrl: 'https://quantic.edu/ms-business-analytics/',
            programType: ProgramType.msba,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Quantic / 26 months / 14% APR', 'Quantic / 52 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: (cohort: CohortContext | null) =>
            `The Quantic MS in ${
                cohort && isPreMsbaAppliedAiRename(cohort)
                    ? MSBA_PRE_RENAME_BUSINESS_ANALYTICS_TAIL
                    : MSBA_POST_RENAME_BUSINESS_ANALYTICS_TAIL
            }`,
    }),
    [ProgramType.msse]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultQuanticConfig,
        programFamily: ProgramFamily.master_of_science_in_software_engineering,
        applicationFormConfigs: ApplicationFormConfigsByProgramType[ProgramType.msse],
        key: ProgramType.msse,
        programField: ProgramField.master_of_science_in_software_engineering,
        isMSSE: true,
        learnMoreUrlBrandConfigProperty: 'learnMoreAboutMsseUrl',
        label: 'MSSE', // internal
        supportEmailUsername: 'msse',
        shortProgramTitle: 'MSSE',
        fullTitle: () => 'MS in Software Engineering',
        openCoursesSidebarBoxContentKey: 'content_open_courses_software_engineering',
        applicationReceivedModalContinueCoursesKey: 'continue_courses_software_engineering',
        programBoxNotAppliedMobileHtmlKey: 'msse_program',
        applicationReceivedBoxPlaylistTitleKey: 'softwareEngineering',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_msse',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1650 * 13,
            numMonthlyIntervals: 13,
        },
        welcomePackageConfiguration: welcomePackageConfigs.msse,
        transcriptProgramTitle: () => 'Master of Science in Software Engineering',
        libraryDatabases: ['one_business'],
        studentSpotlightCarouselProps: {
            streamIds: [
                '15dec0e73019fc35d840529e7a3e987a',
                '8d26a4bb2df39b1a4ef4b5b6cb662448',
                '50fb20d27a45adffd063700b7f5b3327',
                '8889a693db6348253a1c6b997299506c',
                '020574561588721776f193ad4a2205a7',
            ],
        },
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: msse,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_msse' },
            description: { key: 'settings.select_program.program_msse_description' },
            preapprovedDescription: { key: 'settings.select_program.program_msse_preapproved_description' },
            learnMoreUrl: 'https://quantic.edu/ms-software-engineering/',
            programType: ProgramType.msse,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Quantic / 26 months / 14% APR', 'Quantic / 52 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: () => 'The Quantic MS in Software Engineering',
        programSwitcherMenuTitle: () => 'Master of Science in Software Engineering',
        programAchievementGraphicProgramTitleLines: () => ['Master of Science in', 'Software Engineering'],
        acceptedScreenTitleHtml: () => 'MS in Software Engineering',
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic MS in <br class="max-md:hidden"> Software Engineering',
    }),
    [ProgramType.msaie]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultQuanticConfig,
        programFamily: ProgramFamily.master_of_science_in_software_engineering,
        applicationFormConfigs: ApplicationFormConfigsByProgramType[ProgramType.msaie],
        key: ProgramType.msaie,
        programField: ProgramField.master_of_science_in_software_engineering,
        isMSAIE: true,
        learnMoreUrlBrandConfigProperty: 'learnMoreAboutMsaieUrl',
        label: 'MSAIE', // internal
        supportEmailUsername: 'msaie',
        shortProgramTitle: 'MSAIE',
        fullTitle: () => 'MS in AI Engineering',
        openCoursesSidebarBoxContentKey: 'content_open_courses_ai_engineering',
        applicationReceivedModalContinueCoursesKey: 'continue_courses_ai_engineering',
        programBoxNotAppliedMobileHtmlKey: 'msaie_program',
        applicationReceivedBoxPlaylistTitleKey: 'aiEngineering',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_msaie',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1650 * 13,
            numMonthlyIntervals: 13,
        },
        welcomePackageConfiguration: welcomePackageConfigs.msaie,
        transcriptProgramTitle: () => 'Master of Science in Artificial Intelligence Engineering',
        libraryDatabases: ['one_business'],
        studentSpotlightCarouselProps: {
            // Using MSSE student spotlights - update when MSAIE-specific content is available
            streamIds: [
                '15dec0e73019fc35d840529e7a3e987a',
                '8d26a4bb2df39b1a4ef4b5b6cb662448',
                '50fb20d27a45adffd063700b7f5b3327',
                '8889a693db6348253a1c6b997299506c',
                '020574561588721776f193ad4a2205a7',
            ],
        },
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: msaie,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_msaie' },
            description: { key: 'settings.select_program.program_msaie_description' },
            preapprovedDescription: { key: 'settings.select_program.program_msaie_preapproved_description' },
            learnMoreUrl: 'https://quantic.edu/ms-ai-engineering/',
            programType: ProgramType.msaie,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Quantic / 26 months / 14% APR', 'Quantic / 52 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: () => 'The Quantic MS in AI Engineering',
        programSwitcherMenuTitle: () => 'Master of Science in AI Engineering',
        programAchievementGraphicProgramTitleLines: () => ['Master of Science in', 'AI Engineering'],
        acceptedScreenTitleHtml: () => 'MS in AI Engineering',
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic MS in <br class="max-md:hidden"> AI Engineering',
    }),
    /* Quantic Degree Programs */

    /* Valar Degree Programs */
    [ProgramType.mba_leadership]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultValarConfig,
        ...isMBA,
        key: ProgramType.mba_leadership,
        label: 'MBALM', // internal
        supportEmailUsername: 'mba',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_mba_emba',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1225 * 12,
            numMonthlyIntervals: 12,
        },
        welcomePackageConfiguration: welcomePackageConfigs.mbaLeadership,
        transcriptProgramTitle: () => 'Master of Business Administration in Leadership and Management',
        studentSpotlightCarouselProps: valarMbaAndEmbaStudentSpotlightCarouselProps,
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: mba,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_mba' },
            description: { key: 'settings.select_program.program_mba_description' },
            learnMoreUrl: 'https://valar.quantic.edu/mba/',
            programType: ProgramType.mba_leadership,
            isVisible: (institutionID: InstitutionId) => institutionID === InstitutionId.valar,
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Valar / 24 months / 14% APR', 'Valar / 48 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: () => 'The Valar MBA',
        programSwitcherMenuTitle: () => 'Master of Business Administration in Leadership and Management',
        programAchievementGraphicProgramTitleLines: () => ['MBA'],
        acceptedScreenTitleHtml: () => 'MBA',
        studentDashboardProgramBoxTitleHtml: () => 'The Valar <br class="max-md:hidden"> MBA',
    }),
    [ProgramType.emba_strategic_leadership]: buildConfig({
        ...DefaultDegreeConfig,
        ...DefaultValarConfig,
        ...isEMBA,
        key: ProgramType.emba_strategic_leadership,
        label: 'EMBASL', // internal
        supportEmailUsername: 'emba',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_mba_emba',
        preOfferDefaultPaymentInformation: {
            baseTuition: 1225 * 12,
            numMonthlyIntervals: 12,
        },
        welcomePackageConfiguration: welcomePackageConfigs.embaStrategicLeadership,
        transcriptProgramTitle: () => 'Executive Master of Business Administration in Strategic Leadership',
        studentSpotlightCarouselProps: valarMbaAndEmbaStudentSpotlightCarouselProps,
        selectProgramListItem: {
            section: SelectProgramSection.degree,
            image: emba,
            imageMode: 'center',
            title: { key: 'settings.select_program.program_emba' },
            description: { key: 'settings.select_program.program_emba_description' },
            learnMoreUrl: 'https://valar.quantic.edu/executive-mba/',
            programType: ProgramType.emba_strategic_leadership,
            isVisible: (institutionID: InstitutionId) => institutionID === InstitutionId.valar,
        },
        additionalFinancingOptions: ADDITIONAL_FINANCING_OPTIONS.filter(o =>
            ['Valar / 24 months / 14% APR', 'Valar / 48 months / 14% APR'].includes(o.label),
        ),
        programBoxTitle: () => 'The Valar EMBA',
        programSwitcherMenuTitle: () => 'Executive Master of Business Administration in Strategic Leadership',
        programAchievementGraphicProgramTitleLines: () => ['Executive MBA'],
        acceptedScreenTitleHtml: () => 'Executive MBA',
        studentDashboardProgramBoxTitleHtml: () => 'The Valar <br class="max-md:hidden"> EMBA',
    }),
    /* Valar Degree Programs */

    /* Exec Ed Certs */
    [ProgramType.ai_for_technical_leaders_cert]: buildConfig({
        ...DefaultTwoMonthExecEdConfig,
        key: ProgramType.ai_for_technical_leaders_cert,
        programField: ProgramField.ai_for_technical_leaders_cert,
        label: 'C-AILEAD',
        shortProgramTitle: 'AI for Technical Leaders',
        fullTitle: () => 'AI for Technical Leaders',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_ai_for_technical_leaders_cert',
        transcriptProgramTitle: () => 'AI for Technical Leaders',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.ai_and_future_of_business,
            image: aiTechLeaders,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_ai_for_technical_leaders_cert' },
            description: {
                key: 'settings.select_program.program_ai_for_technical_leaders_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/ai-for-technical-leaders/',
            programType: ProgramType.ai_for_technical_leaders_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'AI for Technical Leaders',
        programAchievementGraphicProgramTitleLines: () => ['AI for', 'Technical Leaders'],
        studentDashboardProgramBoxTitleHtml: () => 'AI for Technical Leaders',
    }),
    [ProgramType.ai_for_business_cert]: buildConfig({
        ...DefaultTwoMonthExecEdConfig,
        key: ProgramType.ai_for_business_cert,
        programField: ProgramField.ai_for_business_cert,
        label: 'C-AIBIZ',
        shortProgramTitle: 'AI for Business Leaders',
        fullTitle: () => 'AI for Business Leaders',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_ai_for_business_cert',
        transcriptProgramTitle: () => 'AI for Business Leaders',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.ai_and_future_of_business,
            image: aiBusiness, // FIXME: Replace this stub image with the final approved image when it becomes available
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_ai_for_business_cert' },
            description: {
                // FIXME: Replace this stub description with the actual aiBusiness description when it becomes available
                key: 'settings.select_program.program_ai_for_business_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/ai-for-business/',
            programType: ProgramType.ai_for_business_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'AI for Business Leaders',
        programAchievementGraphicProgramTitleLines: () => ['AI for', 'Business Leaders'],
        studentDashboardProgramBoxTitleHtml: () => 'AI for Business Leaders',
    }),
    [ProgramType.bus_analytics_leaders_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.bus_analytics_leaders_cert,
        programField: ProgramField.bus_analytics_leaders_cert,
        numMonths: 3,
        label: 'C-BIZLEAD',
        fullTitle: () => 'Business Analytics for Leaders',
        shortProgramTitle: 'Business Analytics for Leaders',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_bus_analytics_leaders_cert',
        transcriptProgramTitle: () => 'Business Analytics for Leaders',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.data_and_finance,
            image: busAnalyticsLeaders,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_bus_analytics_leaders_cert' },
            description: {
                key: 'settings.select_program.program_bus_analytics_leaders_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/business-analytics-for-leaders/',
            programType: ProgramType.bus_analytics_leaders_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Business Analytics for Leaders',
        programAchievementGraphicProgramTitleLines: () => ['Business Analytics', 'for Leaders'],
        studentDashboardProgramBoxTitleHtml: () => 'Business Analytics for Leaders',
    }),
    [ProgramType.cmo_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.cmo_cert,
        programField: ProgramField.cmo_cert,
        numMonths: 8,
        label: 'C-CMO',
        fullTitle: () => 'CMO Program',
        shortProgramTitle: 'CMO',
        standaloneShortProgramTitle: 'CMO Program',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_cmo_cert',
        transcriptProgramTitle: () => 'Chief Marketing Officer Program',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.rise_to_c_suite,
            image: cmoProgram,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_cmo_cert' },
            description: { key: 'settings.select_program.program_cmo_cert_description' },
            learnMoreUrl: 'https://quantic.edu/executive-education/quantic-cmo-program/',
            programType: ProgramType.cmo_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'CMO Program',
        programAchievementGraphicProgramTitleLines: () => ['Chief Marketing Officer', 'Program'],
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic CMO Program',
    }),
    [ProgramType.cto_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.cto_cert,
        programField: ProgramField.cto_cert,
        numMonths: 8,
        label: 'C-CTO',
        fullTitle: () => 'CTO Program',
        shortProgramTitle: 'CTO',
        standaloneShortProgramTitle: 'CTO Program',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_cto_cert',
        transcriptProgramTitle: () => 'Chief Technology Officer Program',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.rise_to_c_suite,
            image: ctoProgram,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_cto_cert' },
            description: { key: 'settings.select_program.program_cto_cert_description' },
            learnMoreUrl: 'https://quantic.edu/executive-education/quantic-cto-program/',
            programType: ProgramType.cto_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'CTO Program',
        programAchievementGraphicProgramTitleLines: () => ['Chief Technology Officer', 'Program'],
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic CTO Program',
    }),
    [ProgramType.data_science_foundations_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.data_science_foundations_cert,
        programField: ProgramField.data_science_foundations_cert,
        numMonths: 4,
        label: 'C-DSF',
        fullTitle: () => 'Data Science Foundations',
        shortProgramTitle: 'Data Science Foundations',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_data_science_foundations_cert',
        transcriptProgramTitle: () => 'Data Science Foundations',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.data_and_finance,
            image: dataScienceFoundations,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_data_science_foundations_cert' },
            description: {
                key: 'settings.select_program.program_data_science_foundations_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/data-science-foundations/',
            programType: ProgramType.data_science_foundations_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Data Science Foundations',
        programAchievementGraphicProgramTitleLines: () => ['Data Science', 'Foundations'],
        studentDashboardProgramBoxTitleHtml: () => 'Data Science Foundations',
    }),
    [ProgramType.executive_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.executive_cert,
        programField: ProgramField.executive_cert,
        numMonths: 8,
        label: 'C-EXEC',
        fullTitle: () => 'Executive Program',
        shortProgramTitle: 'Executive',
        standaloneShortProgramTitle: 'Executive Program',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_executive_cert',
        transcriptProgramTitle: () => 'Executive Program',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.rise_to_c_suite,
            image: executiveProgram,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_executive_cert' },
            description: { key: 'settings.select_program.program_executive_cert_description' },
            learnMoreUrl: 'https://quantic.edu/executive-education/quantic-executive-program/',
            programType: ProgramType.executive_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Executive Program',
        programAchievementGraphicProgramTitleLines: () => ['Executive Program'],
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic Executive Program',
    }),
    [ProgramType.fin_for_non_fin_managers_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.fin_for_non_fin_managers_cert,
        programField: ProgramField.fin_for_non_fin_managers_cert,
        numMonths: 3,
        label: 'C-FIN',
        fullTitle: () => 'Finance & Accounting for Managers',
        shortProgramTitle: 'Finance & Accounting for Managers',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_fin_for_managers_cert',
        transcriptProgramTitle: () => 'Finance & Accounting for Managers',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.data_and_finance,
            image: financeAccountingManagers,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_fin_for_non_fin_managers_cert' },
            description: {
                key: 'settings.select_program.program_fin_for_non_fin_managers_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/finance-and-accounting-for-managers/',
            programType: ProgramType.fin_for_non_fin_managers_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Finance & Accounting for Managers',
        programAchievementGraphicProgramTitleLines: () => ['Finance & Accounting', 'for Managers'],
        studentDashboardProgramBoxTitleHtml: () => 'Finance & Accounting for Managers',
    }),
    [ProgramType.founders_cert]: buildConfig({
        ...DefaultExecEdConfig,
        key: ProgramType.founders_cert,
        programField: ProgramField.founders_cert,
        numMonths: 8,
        label: 'C-FOUND',
        fullTitle: () => 'Founders Program',
        shortProgramTitle: 'Founders',
        standaloneShortProgramTitle: 'Founders Program',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_founders_cert',
        transcriptProgramTitle: () => 'Founders Program',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.rise_to_c_suite,
            image: foundersProgram,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_founders_cert' },
            description: { key: 'settings.select_program.program_founders_cert_description' },
            learnMoreUrl: 'https://quantic.edu/executive-education/quantic-founders-program/',
            programType: ProgramType.founders_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Founders Program',
        programAchievementGraphicProgramTitleLines: () => ['Founders Program'],
        studentDashboardProgramBoxTitleHtml: () => 'The Quantic Founders Program',
    }),
    // learn_code_gpt_cert has been retired
    [ProgramType.learn_code_gpt_cert]: buildConfig({
        ...DefaultTwoMonthExecEdConfig,
        key: ProgramType.learn_code_gpt_cert,
        programField: ProgramField.learn_code_gpt_cert,
        label: 'C-CODEAI',
        fullTitle: () => 'Learn to Code Using ChatGPT',
        shortProgramTitle: 'Learn to Code Using ChatGPT',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_learn_code_gpt_cert',
        transcriptProgramTitle: () => 'Learn to Code Using ChatGPT',
        programSwitcherMenuTitle: () => 'Learn to Code Using ChatGPT',
        programAchievementGraphicProgramTitleLines: () => ['Learn to Code', 'Using ChatGPT'],
        studentDashboardProgramBoxTitleHtml: () => 'Learn to Code Using ChatGPT',
    }),
    [ProgramType.prototype_gpt_cert]: buildConfig({
        ...DefaultTwoMonthExecEdConfig,
        key: ProgramType.prototype_gpt_cert,
        programField: ProgramField.prototype_gpt_cert,
        label: 'C-PBIZ',
        fullTitle: () => 'AI for Building Software',
        shortProgramTitle: 'AI for Building Software',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_prototype_gpt_cert',
        transcriptProgramTitle: () => 'AI for Building Software',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.ai_and_future_of_business,
            image: prototypeGpt,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_prototype_gpt_cert' },
            description: {
                key: 'settings.select_program.program_prototype_gpt_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/ai-for-building-software/',
            programType: ProgramType.prototype_gpt_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'AI for Building Software',
        programAchievementGraphicProgramTitleLines: () => ['AI for', 'Building Software'],
        studentDashboardProgramBoxTitleHtml: () => 'AI for Building Software',
    }),
    [ProgramType.tyb_ai_cert]: buildConfig({
        ...DefaultTwoMonthExecEdConfig,
        key: ProgramType.tyb_ai_cert,
        programField: ProgramField.tyb_ai_cert,
        label: 'C-TBIZAI',
        fullTitle: () => 'Transform Your Business with AI & ChatGPT',
        shortProgramTitle: 'Transform Your Business with AI & ChatGPT',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_tyb_ai_cert',
        transcriptProgramTitle: () => 'Transform Your Business with AI & ChatGPT',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.ai_and_future_of_business,
            image: tybAi,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_tyb_ai_cert' },
            description: {
                key: 'settings.select_program.program_tyb_ai_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/transform-your-business-with-ai-and-chatgpt/',
            programType: ProgramType.tyb_ai_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Transform Your Business with AI & ChatGPT',
        programAchievementGraphicProgramTitleLines: () => ['Transform Your Business', 'with AI & ChatGPT'],
        studentDashboardProgramBoxTitleHtml: () => 'Transform Your Business with AI & ChatGPT',
    }),
    [ProgramType.tyb_blockchain_cert]: buildConfig({
        ...DefaultTwoMonthExecEdConfig,
        key: ProgramType.tyb_blockchain_cert,
        programField: ProgramField.tyb_blockchain_cert,
        label: 'C-TBIZBC',
        fullTitle: () => 'Transform Your Business with Blockchain',
        shortProgramTitle: 'Transform Your Business with Blockchain',
        applicationProgramTypeHeader: 'settings.settings.application_program_type_tyb_blockchain_cert',
        transcriptProgramTitle: () => 'Transform Your Business with Blockchain',
        selectProgramListItem: {
            section: SelectProgramSection.execEd,
            subSection: ExecEdCertCategory.ai_and_future_of_business,
            image: tybBlockchain,
            imageMode: 'cover',
            title: { key: 'settings.select_program.program_tyb_blockchain_cert' },
            description: {
                key: 'settings.select_program.program_tyb_blockchain_cert_description',
            },
            learnMoreUrl: 'https://quantic.edu/executive-education/transform-your-business-with-blockchain/',
            programType: ProgramType.tyb_blockchain_cert,
            isVisible(_institutionID: InstitutionId) {
                return true;
            },
        },
        programSwitcherMenuTitle: () => 'Transform Your Business with Blockchain',
        programAchievementGraphicProgramTitleLines: () => ['Transform Your Business', 'with Blockchain'],
        studentDashboardProgramBoxTitleHtml: () => 'Transform Your Business with Blockchain',
    }),
    /* Exec Ed Certs */

    /* VLA */
    [ProgramType.vla]: buildConfig({
        ...DefaultVLAProgramTypeConfig,
        key: ProgramType.vla,
        label: 'VLA', // internal
        fullTitle: () => 'Valar Leadership Accelerator',
        transcriptProgramTitle: () => 'Valar Leadership Accelerator',
        programSwitcherMenuTitle: () => 'Valar Leadership Accelerator',
        programAchievementGraphicProgramTitleLines: () => [],
        studentDashboardProgramBoxTitleHtml: () => 'The Valar <br class="max-md:hidden"> Leadership Accelerator',
    }),
    [ProgramType.vla_aspiring_managers]: buildConfig({
        ...DefaultVLAProgramTypeConfig,
        key: ProgramType.vla_aspiring_managers,
        label: 'VLA-AM', // internal
        fullTitle: () => 'Valar Leadership Accelerator for Aspiring Leaders and Managers',
        transcriptProgramTitle: () => 'Valar Leadership Accelerator for Aspiring Leaders and Managers',
        programSwitcherMenuTitle: () => 'Valar Leadership Accelerator for Aspiring Leaders and Managers',
        programAchievementGraphicProgramTitleLines: () => [],
        studentDashboardProgramBoxTitleHtml: () =>
            'The Valar Leadership Accelerator <br class="max-md:hidden"> for Aspiring Leaders and Managers',
    }),
    /* VLA */

    /* Other programs */
    [ProgramType.the_business_certificate]: buildConfig({
        ...DefaultCertificateConfig,
        ...DefaultSmartlyConfig,
        programFamily: ProgramFamily.the_business_certificate,
        key: ProgramType.the_business_certificate,
        supportsEditingCareerProfile: true,
        programField: ProgramField.the_business_certificate,
        institutionID: InstitutionId.smartly,
        label: 'The Business Certificate (free)',
        fullTitle: () => 'Fundamentals of Business Certificate',
        shortProgramTitle: 'Fundamentals of Business Certificate',
        completionDocumentType: DYNAMIC_DOWNLOADABLE_CERTIFICATE,
        supportsProgramApplications: true,
        libraryDatabases: [],
        programSwitcherMenuTitle: () => 'The Business Certificate',
        programAchievementGraphicProgramTitleLines: () => [],
    }),
    [ProgramType.jordanian_math]: buildConfig({
        ...DefaultConfig,
        supportsAvatarUploadInAccountPage: false,
        programFamily: ProgramFamily.jordanian_math,
        key: ProgramType.jordanian_math,
        programField: ProgramField.jordanian_math,
        institutionID: InstitutionId.miya_miya,
        branding: Brand.miya_miya,
        label: 'Jordanian Math',
        shortProgramTitle: 'High School STEM',
        fullTitle: () => '',
        libraryDatabases: [],
        supportEmailDomain: SMARTLY_DOMAIN,
        supportEmailUsername: 'support',
        programSwitcherMenuTitle: () => 'Math (Jordan)',
        programAchievementGraphicProgramTitleLines: () => [],
    }),
    [ProgramType.external]: buildConfig({
        // External
        // This is the minimal config necessary (that I've identified so far) for an external
        // program type to function with a career profile. It's not necessarily intended to be
        // used long-term, but it's useful for now to stop the Applicant Admin -> Edit Profile
        // page from throwing errors when it looks up these keys.
        ...DefaultConfig,
        ...DefaultSmartlyConfig,
        key: ProgramType.external,
        programField: null,
        label: 'External',
        shortProgramTitle: '',
        fullTitle: () => '',
        supportsStudentDashboardConfig: false,
        libraryDatabases: [],
        supportsWelcomeBox: true,
        programSwitcherMenuTitle: () => 'External',
        programAchievementGraphicProgramTitleLines: () => [],
    }),
    // The `demo` program type was not defined in schedulable.js.
    // Because it is defined in the ProgramType type, typescript wants entries for it
    // All of these values should be unused in practice, so it doesn't matter what they're set to
    [ProgramType.demo]: buildConfig({
        ...DefaultConfig,
        ...DefaultQuanticConfig,
        key: ProgramType.demo,
        programField: null,
        label: '',
        shortProgramTitle: '',
        fullTitle: () => '',
        transcriptProgramTitle: () => '',
        libraryDatabases: [],
        programSwitcherMenuTitle: () => 'Demo',
        programAchievementGraphicProgramTitleLines: () => [],
    }),
    /* Other programs */

    /* Deprecated Programs */
    [ProgramType.career_network_only]: buildConfig({
        ...DefaultConfig,
        ...DefaultSmartlyConfig,
        key: ProgramType.career_network_only,
        supportsEditingCareerProfile: true,
        programField: ProgramField.career_network_only,
        label: 'Careers Network Only', // internal
        shortProgramTitle: '',
        fullTitle: () => '',
        transcriptProgramTitle: () => 'Careers Network Only',
        libraryDatabases: [],
        supportsWelcomeBox: true,
        programSwitcherMenuTitle: () => 'Careers Network Only',
        programAchievementGraphicProgramTitleLines: () => [],
        studentDashboardProgramBoxTitleHtml: () => 'Smartly <br> Talent',
    }),
    /* Deprecated Programs */
};
